import { comCorpo, pedir } from '@/lib/pedido';

/**
 * A folha de campo (0035; decisões 9, 10 e 12): a porta para o backend e a
 * parte que não depende de tela.
 */

export type Instrumento = 'caneta' | 'lapis' | 'misto';
export const ROTULO_DO_INSTRUMENTO: Readonly<Record<Instrumento, string>> = {
  caneta: 'Caneta',
  lapis: 'Lápis',
  misto: 'Caneta e lápis',
};

export const CAMPOS = ['data', 'hora', 'medidor', 'litros', 'operador'] as const;
export type Campo = (typeof CAMPOS)[number];

export interface FolhaDaApi {
  readonly id: string;
  readonly codigo: string;
  readonly de: string;
  readonly ate: string;
  readonly emitidaEm: string;
  readonly equipamentoId: string;
  readonly equipamentoCodigo: string;
  readonly equipamentoDescricao: string;
  readonly tipoMedidor: 'horimetro' | 'odometro' | null;
  readonly tanqueId: string;
  readonly tanqueCodigo: string;
  readonly tanqueNome: string;
  readonly produtoNome: string;
  readonly empresa: string;
  readonly emitidaPor: string | null;
  readonly leituras: number;
  readonly conferidas: number;
  readonly lancados: number;
}

export interface LinhaLidaDaApi {
  readonly linha: number;
  readonly data: string | null;
  readonly hora: string | null;
  readonly medidor: number | null;
  readonly litros: number | null;
  readonly operador: string | null;
  readonly operadorId: string | null;
  readonly duvidas: readonly Campo[];
}

export interface LeituraDaApi {
  readonly id: string;
  readonly folhaId: string;
  readonly mime: string;
  readonly instrumento: Instrumento;
  readonly linhasLidas: readonly LinhaLidaDaApi[];
  readonly lidaEm: string;
  readonly lidaPor: string | null;
  readonly conferidaEm: string | null;
  readonly conferidaPor: string | null;
  readonly camposLidos: number | null;
  readonly camposCorrigidos: number | null;
  readonly abastecimentosLancados: number | null;
}

export interface AcertoDaApi {
  readonly instrumento: Instrumento;
  readonly leituras: number;
  readonly camposLidos: number;
  readonly camposCorrigidos: number;
  readonly taxaDeAcerto: number | null;
}

export interface LinhaEnviada {
  readonly origem: number | null;
  readonly incluir: boolean;
  readonly data: string;
  readonly hora: string;
  readonly medidor: number | null;
  readonly litros: number;
  readonly operadorId: string | null;
}

export const apiDaFolha = {
  folhas: () =>
    pedir<{ folhas: readonly FolhaDaApi[]; acerto: readonly AcertoDaApi[]; leituraLigada: boolean }>('/combustivel/folhas'),
  folha: (id: string) =>
    pedir<{ folha: FolhaDaApi; leituras: readonly LeituraDaApi[]; pessoas: readonly { id: string; nome: string }[] }>(
      `/combustivel/folhas/${encodeURIComponent(id)}`,
    ),
  emitir: (envio: { equipamentoId: string; tanqueId: string; de: string; ate: string }) =>
    comCorpo<FolhaDaApi>('POST', '/combustivel/folhas', envio),
  /** A imagem vai crua: o servidor lê o tipo pelo conteúdo. */
  escanear: (folhaId: string, instrumento: Instrumento, arquivo: File) =>
    pedir<{ leitura: LeituraDaApi; codigoLido: string | null; codigoConfere: boolean | null }>(
      `/combustivel/folhas/${encodeURIComponent(folhaId)}/leituras?instrumento=${instrumento}`,
      { method: 'POST', body: arquivo, headers: { 'content-type': arquivo.type || 'application/octet-stream' } },
    ),
  imagem: (leituraId: string) =>
    pedir<{ url: string; mime: string }>(`/combustivel/folhas/leituras/${encodeURIComponent(leituraId)}/arquivo`),
  conferir: (leituraId: string, linhas: readonly LinhaEnviada[]) =>
    comCorpo<{ lancados: number; camposLidos: number; camposCorrigidos: number }>(
      'POST',
      `/combustivel/folhas/leituras/${encodeURIComponent(leituraId)}/conferencia`,
      { linhas },
    ),
};

/** Uma linha na tela da conferência: tudo texto, como o campo de formulário guarda. */
export interface LinhaEmConferencia {
  readonly chave: string;
  readonly origem: number | null;
  readonly incluir: boolean;
  readonly data: string;
  readonly hora: string;
  readonly medidor: string;
  readonly litros: string;
  readonly operadorId: string;
  readonly duvidas: readonly Campo[];
  readonly operadorEscrito: string | null;
}

const numeroParaCampo = (n: number | null) => (n === null ? '' : String(n).replace('.', ','));

/** A conferência abre com o que a IA leu, e marca como dúvida o campo que ela não conseguiu ler. */
export function linhasParaConferir(lidas: readonly LinhaLidaDaApi[]): LinhaEmConferencia[] {
  return lidas.map((l) => {
    const duvidas = new Set<Campo>(l.duvidas);
    if (l.data === null) duvidas.add('data');
    if (l.hora === null) duvidas.add('hora');
    if (l.litros === null) duvidas.add('litros');
    if (l.operador !== null && l.operadorId === null) duvidas.add('operador');
    return {
      chave: `lida-${String(l.linha)}`,
      origem: l.linha,
      incluir: true,
      data: l.data ?? '',
      hora: l.hora ?? '',
      medidor: numeroParaCampo(l.medidor),
      litros: numeroParaCampo(l.litros),
      operadorId: l.operadorId ?? '',
      duvidas: [...duvidas],
      operadorEscrito: l.operador,
    };
  });
}

/** O número do campo, na escrita brasileira ("1.230,5"). Vazio devolve `null`; inválido, `NaN`. */
export function numeroDoCampo(texto: string): number | null {
  const t = texto.trim();
  if (t === '') return null;
  const n = Number(t.includes(',') ? t.replace(/\./g, '').replace(',', '.') : t);
  return Number.isFinite(n) && n >= 0 ? n : Number.NaN;
}

/**
 * Da tela ao envio, com a primeira recusa escrita em frase. Linha desmarcada
 * vai também: é assim que o servidor conta a linha que a IA inventou.
 */
export function montarEnvio(
  linhas: readonly LinhaEmConferencia[],
  periodo: { readonly de: string; readonly ate: string },
): { readonly linhas: LinhaEnviada[] } | { readonly recusa: string } {
  const envio: LinhaEnviada[] = [];
  for (const [i, l] of linhas.entries()) {
    const n = `Linha ${String(l.origem ?? `acrescentada ${String(i + 1)}`)}`;
    if (!l.incluir) {
      if (l.origem !== null) envio.push({ origem: l.origem, incluir: false, data: periodo.de, hora: '00:00', medidor: null, litros: 1, operadorId: null });
      continue;
    }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(l.data)) return { recusa: `${n}: falta a data.` };
    if (l.data < periodo.de || l.data > periodo.ate) return { recusa: `${n}: a data está fora do período da folha.` };
    if (!/^([01]\d|2[0-3]):[0-5]\d$/.test(l.hora)) return { recusa: `${n}: falta a hora, no formato HH:MM.` };
    const litros = numeroDoCampo(l.litros);
    if (litros === null || Number.isNaN(litros) || litros <= 0) return { recusa: `${n}: os litros precisam ser um número maior que zero.` };
    const medidor = numeroDoCampo(l.medidor);
    if (medidor !== null && Number.isNaN(medidor)) return { recusa: `${n}: o medidor não é um número.` };
    envio.push({ origem: l.origem, incluir: true, data: l.data, hora: l.hora, medidor, litros, operadorId: l.operadorId || null });
  }
  return { linhas: envio };
}

/** O período padrão de uma folha nova: o mês corrente. */
export function mesCorrente(hoje: Date): { readonly de: string; readonly ate: string } {
  const ano = hoje.getFullYear();
  const mes = hoje.getMonth();
  const doisDigitos = (n: number) => String(n).padStart(2, '0');
  const ultimo = new Date(ano, mes + 1, 0).getDate();
  return { de: `${String(ano)}-${doisDigitos(mes + 1)}-01`, ate: `${String(ano)}-${doisDigitos(mes + 1)}-${doisDigitos(ultimo)}` };
}
